import { CalendarToday, Email, Home, Person, WorkspacePremiumOutlined } from "@mui/icons-material";
import { Card, CardContent, Chip, Divider, Grid, Typography } from "@mui/material";
import moment from "moment";
import React from "react";

import InfoItemWithCopy from "../../../../shared/components/InfoItemWithCopy/InfoItemWithCopy";
import { globalBackground, globalColorText, globalTranslate } from "../../../../shared/utils";
import { globalTranslateSinister } from "../../../../shared/utils/globalTranslateSinister";
import { ISinisterList } from "../../../../shared/services/api/sinister/SinisterService";

interface SinisterBasicCardDataProps {
	row: ISinisterList;
	responsibleName: string;
	totalBordero: number;
	sumLmi?: {
		lmiTotal?: number | string | null;
		saldo?: number | string | null;
	} | null;
}

const parseNumberAnyLocale = (value: number | string): number | null => {
	if (typeof value === "number") return Number.isFinite(value) ? value : null;

	const raw = String(value).trim();
	if (!raw) return null;

	// Mantém apenas dígitos, sinais e separadores conhecidos
	const cleaned = raw.replace(/[^0-9,.-]/g, "");
	if (!cleaned) return null;

	const lastComma = cleaned.lastIndexOf(",");
	const lastDot = cleaned.lastIndexOf(".");

	let normalized = cleaned;
	if (lastComma !== -1 && lastDot !== -1) {
		// O separador que aparece por último costuma ser o decimal
		const decimalSep = lastComma > lastDot ? "," : ".";
		const thousandSep = decimalSep === "," ? "." : ",";
		normalized = normalized.split(thousandSep).join("");
		normalized = normalized.replace(decimalSep, ".");
	} else if (lastComma !== -1) {
		normalized = normalized.replace(",", ".");
	}

	const parsed = Number(normalized);
	return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyBRL = (value?: number | string | null) => {
	if (value === null || value === undefined || value === "") return "";
	const parsed = parseNumberAnyLocale(value);
	if (parsed === null) return "";
	return parsed.toLocaleString("pt-BR", {
		style: "currency",
		currency: "BRL",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

export const SinisterBasicCardData: React.FC<SinisterBasicCardDataProps> = ({ row, responsibleName, totalBordero, sumLmi }) => {
	const tenantName = row?.proposal?.name ?? "";
	const certificate = row?.proposal?.certificateNumber ?? "";
	const createdAt = row?.createdAt ?? "";
	const reportedAt = row?.reportedAt ?? "";
	const closedAt = row?.finalizedAt ?? row?.anceledAt ?? "";
	const busyProperty = row?.busyProperty ?? "";
	const partnerEmail = row?.client?.email ?? row?.email ?? "";
	const responsibleOpen = (row as any)?.user?.name ?? row?.client?.responsible_name ?? "";

	const colProps = { xs: 12 as const, sm: 6 as const, md: 3 as const };

	return (
		<Card sx={{ width: "calc(100% - 8px)", borderRadius: 4, ml: 1 }}>
			<CardContent>
				<Grid container spacing={2}>
					<Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6" color="primary">
							Dados do sinistro
						</Typography>
						<Chip
							size="medium"
							label={row?.status ? globalTranslateSinister(row.status, "sinister") : ""}
							sx={{
								color: globalColorText(row?.status ?? "open"),
								background: globalBackground(row?.status ?? "open"),
								fontWeight: 500,
							}}
						/>
					</Grid>

					<Grid item xs={12}>
						<Divider />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Inquilino" value={tenantName} icon={<Person />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Certificado" value={certificate} icon={<WorkspacePremiumOutlined />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Responsável abertura sinistro" value={responsibleOpen} icon={<Person />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy
							label="Criada"
							value={createdAt ? moment(createdAt).format("L") : ""}
							additionalInfo={createdAt ? moment(createdAt).startOf("day").fromNow() : ""}
							icon={<CalendarToday />}
						/>
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Abertura sinistro" value={reportedAt ? moment(reportedAt).format("L") : ""} icon={<CalendarToday />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Encerramento sinistro" value={closedAt ? moment(closedAt).format("L") : ""} icon={<CalendarToday />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Total de borderôs" value={String(totalBordero ?? 0)} icon={<WorkspacePremiumOutlined />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Situação do imóvel" value={busyProperty ? globalTranslate(busyProperty) : ""} icon={<Home />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Responsável pelo sinistro" value={responsibleName ?? ""} icon={<Person />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Email do parceiro" value={partnerEmail} icon={<Email />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="LMI total" value={formatCurrencyBRL(sumLmi?.lmiTotal)} icon={<WorkspacePremiumOutlined />} />
					</Grid>

					<Grid item {...colProps}>
						<InfoItemWithCopy label="Saldo LMI" value={formatCurrencyBRL(sumLmi?.saldo)} icon={<WorkspacePremiumOutlined />} />
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
};
